import type { BillboardListParams } from '@/features/billboards/api/billboardApi';

export type BillboardListItem = {
  _id?: string;
  id?: string;
  screenName?: string;
  name?: string;
  basePrice?: number;
  totalViews?: number;
  avgViews?: number;
  femaleViews?: number;
  maleViews?: number;
  footfallCount?: number;
  footfall?: number;
  screenOrientation?: string;
  screenType?: string;
  locationType?: string;
  venueType?: string;
  VenueType?: string;
  VenueSubType?: string;
  latitude?: number | string;
  longitude?: number | string;
  lattitude?: number | string;
  createdAt?: string;
  isWishlisted?: boolean;
  thumbnail?: { url?: string };
  address?: {
    building?: string;
    area?: string;
    locality?: string;
    street?: string;
    fullAddress?: string;
    secondaryAddress?: string;
    district?: string;
    city?: string;
    state?: string;
    latitude?: number | string;
    longitude?: number | string;
  };
};

export type BillboardListPagination = {
  page?: number;
  pages?: number;
  totalPages?: number;
  total?: number;
  totalCount?: number;
  limit?: number;
};

export type BillboardListResponse = {
  data?: {
    billboards?: BillboardListItem[];
    pagination?: BillboardListPagination;
  };
  pagination?: BillboardListPagination;
  message?: string;
};

export function resolveBillboardListPagination(
  pagination: BillboardListPagination | undefined,
  itemCount: number,
  fallback: { page: number; limit: number },
): {
  page: number;
  limit: number;
  total: number;
  pages: number;
} {
  const page = Math.max(1, Number(pagination?.page) || fallback.page);
  const limit = Math.max(1, Number(pagination?.limit) || fallback.limit);
  const total = Math.max(
    0,
    Number(pagination?.total ?? pagination?.totalCount) || itemCount,
  );
  const reportedPages =
    Number(pagination?.pages ?? pagination?.totalPages) || 0;
  const fromTotal = limit > 0 && total > 0 ? Math.ceil(total / limit) : 0;




  const pages = Math.max(1, fromTotal || reportedPages || 1);

  const clampedPage = total > 0 ? Math.min(page, pages) : page;
  return { page: clampedPage, limit, total, pages };
}

export const ORIENTATION_OPTIONS = [
  { value: '', label: 'All orientations' },
  { value: 'PORTRAIT', label: 'Portrait' },
  { value: 'LANDSCAPE', label: 'Landscape' },
  { value: 'SQUARE', label: 'Square' },
] as const;

export const SORT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'NAME_ASC', label: 'Name (A–Z)' },
  { value: 'NAME_DESC', label: 'Name (Z–A)' },
  { value: 'PRICE_ASC', label: 'Price: low to high' },
  { value: 'PRICE_DESC', label: 'Price: high to low' },
  { value: 'NEWEST', label: 'Newest first' },
] as const;

export function resolveListTypeFromTitle(title?: string) {
  if (title === 'Trending Screens') return 'TRENDING';
  if (title === 'Most Viewed Screens') return 'MOST_VIEWED';
  if (title === 'Newly Launched Screens') return 'NEWLY_LAUNCHED';
  return undefined;
}

export function formatListMetric(value?: number | string | null) {
  if (value === null || value === undefined || value === '') return '0';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(Math.round(num));
}

export function buildScreenLocation(item: {
  area?: string;
  locality?: string;
  city?: string;
  district?: string;
  street?: string;
  building?: string;
  secondaryAddress?: string;
  address?: {
    area?: string;
    locality?: string;
    city?: string;
    district?: string;
    street?: string;
    building?: string;
    secondaryAddress?: string;
  };
}) {
  const address = item.address || {};
  const area = (
    address.area ||
    item.area ||
    address.locality ||
    item.locality ||
    ''
  ).trim();
  const locality = (
    area ||
    address.secondaryAddress ||
    item.secondaryAddress ||
    address.district ||
    item.district ||
    address.street ||
    item.street ||
    address.building ||
    item.building ||
    ''
  ).trim();
  const city = (address.city || item.city || '').trim();

  return [locality, city]
    .filter(Boolean)
    .filter(
      (part, index, parts) =>
        parts.findIndex((item) => item.toLowerCase() === part.toLowerCase()) === index,
    )
    .join(', ');
}

function normalizeAreaText(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function cleanAlphaNumeric(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function matchesAreaQuery(
  item: {
    screenName?: string;
    name?: string;
    locality?: string;
    area?: string;
    secondaryAddress?: string;
    street?: string;
    building?: string;
    district?: string;
    city?: string;
    address?: {
      area?: string;
      locality?: string;
      street?: string;
      building?: string;
      fullAddress?: string;
      secondaryAddress?: string;
      district?: string;
      city?: string;
    };
  },
  areaQuery?: string,
) {
  const needle = normalizeAreaText(areaQuery || '');
  if (!needle) return true;
  const cleanNeedle = cleanAlphaNumeric(needle);

  const address = item.address || {};
  const candidates = [
    address.area,
    item.area,
    item.locality,
    item.secondaryAddress,
    item.street,
    item.building,
    item.district,
    item.city,
    item.screenName,
    item.name,
    address.locality,
    address.secondaryAddress,
    address.street,
    address.building,
    address.fullAddress,
    address.district,
  ];

  return candidates.some(
    (value) => {
      if (!value) return false;
      const text = normalizeAreaText(String(value));
      if (text.includes(needle)) return true;
      if (cleanNeedle.length >= 2 && cleanAlphaNumeric(text).includes(cleanNeedle)) {
        return true;
      }
      return false;
    },
  );
}

/**
 * Compact Indian-style value formatter:
 * 3000 → 3K · 150000 → 1.5L · 25000000 → 2.5C
 */
export function valueConvertor(value?: number | string | null) {
  if (value === null || value === undefined || value === '') return '0';

  const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) return String(value);

  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);

  const format = (n: number, suffix: string) => {
    const rounded = Math.round(n * 10) / 10;
    const text =
      Number.isInteger(rounded) || Math.abs(rounded - Math.round(rounded)) < 1e-9
        ? String(Math.round(rounded))
        : rounded.toFixed(1).replace(/\.0$/, '');
    return `${sign}${text}${suffix}`;
  };

  if (abs >= 1_00_00_000) return format(abs / 1_00_00_000, 'C'); // Crore
  if (abs >= 1_00_000) return format(abs / 1_00_000, 'L'); // Lakh
  if (abs >= 1_000) return format(abs / 1_000, 'K'); // Thousand

  if (!Number.isInteger(abs)) return `${sign}${abs}`;

  return `${sign}${Math.round(abs)}`;
}

export function getBillboardCoords(item: BillboardListItem): {
  lat: number | null;
  lng: number | null;
} {
  const rawLat = item.latitude ?? item.lattitude ?? item.address?.latitude;
  const rawLng = item.longitude ?? item.address?.longitude;

  const lat =
    rawLat !== undefined && rawLat !== null && rawLat !== ''
      ? Number(rawLat)
      : null;
  const lng =
    rawLng !== undefined && rawLng !== null && rawLng !== ''
      ? Number(rawLng)
      : null;

  return {
    lat: lat !== null && Number.isFinite(lat) ? lat : null,
    lng: lng !== null && Number.isFinite(lng) ? lng : null,
  };
}

export function getDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function matchesBillboardFilters(
  item: BillboardListItem,
  filters: BillboardListParams,
): boolean {
  // 1. Orientation matching
  if (filters.screenOrientation) {
    const itemOrientation = (item.screenOrientation || '').trim().toUpperCase();
    const filterOrientation = filters.screenOrientation.trim().toUpperCase();
    if (itemOrientation !== filterOrientation) {
      return false;
    }
  }

  // 2. Location type matching (Indoor, Outdoor, Transit Media, etc.)
  const filterLocationTypes = (
    Array.isArray(filters.locationType)
      ? filters.locationType
      : filters.locationType
        ? [filters.locationType]
        : []
  )
    .filter(Boolean)
    .map((s) => s.toLowerCase());

  if (filterLocationTypes.length > 0) {
    const itemLocationType = (item.locationType || '').toLowerCase();
    const matches = filterLocationTypes.some(
      (lt) =>
        lt === itemLocationType ||
        itemLocationType.includes(lt) ||
        lt.includes(itemLocationType),
    );
    if (!matches) {
      return false;
    }
  }

  // 3. Venue type matching
  const filterVenueTypes = (
    Array.isArray(filters.venueType)
      ? filters.venueType
      : filters.venueType
        ? [filters.venueType]
        : []
  )
    .filter(Boolean)
    .map((s) => s.toLowerCase());

  if (filterVenueTypes.length > 0) {
    const itemVenue = (
      item.venueType ||
      item.VenueType ||
      item.screenType ||
      ''
    ).toLowerCase();
    const matches = filterVenueTypes.some(
      (vt) =>
        vt === itemVenue ||
        itemVenue.includes(vt) ||
        vt.includes(itemVenue),
    );
    if (!matches) {
      return false;
    }
  }

  // 4. Venue sub-type matching
  const filterSubTypes = (
    Array.isArray(filters.VenueSubType)
      ? filters.VenueSubType
      : filters.VenueSubType
        ? [filters.VenueSubType]
        : []
  )
    .filter(Boolean)
    .map((s) => s.toLowerCase());

  if (filterSubTypes.length > 0) {
    const itemSub = (
      item.VenueSubType ||
      (item as Record<string, unknown>).venueSubType ||
      ''
    )
      .toString()
      .toLowerCase();
    const matches = filterSubTypes.some(
      (st) =>
        st === itemSub ||
        itemSub.includes(st) ||
        st.includes(itemSub),
    );
    if (!matches) {
      return false;
    }
  }

  // 5. Area text matching
  if (filters.area && filters.area.trim()) {
    if (!matchesAreaQuery(item, filters.area)) {
      return false;
    }
  }

  // 6. Locality / Coordinates distance matching
  if (
    filters.lat != null &&
    filters.lng != null &&
    Number.isFinite(filters.lat) &&
    Number.isFinite(filters.lng)
  ) {
    const coords = getBillboardCoords(item);
    if (coords.lat != null && coords.lng != null) {
      const radiusKm = filters.radius || 10;
      const distance = getDistanceKm(
        filters.lat,
        filters.lng,
        coords.lat,
        coords.lng,
      );
      if (distance > radiusKm) {
        return false;
      }
    } else if (filters.locality && filters.locality.trim()) {
      if (!matchesAreaQuery(item, filters.locality)) {
        return false;
      }
    }
  } else if (filters.locality && filters.locality.trim()) {
    if (!matchesAreaQuery(item, filters.locality)) {
      return false;
    }
  }

  return true;
}

export function sortBillboards<T extends BillboardListItem>(
  items: T[],
  sortBy?: string,
): T[] {
  if (!sortBy) return items;

  const sorted = [...items];

  switch (sortBy) {
    case 'NAME_ASC':
      return sorted.sort((a, b) => {
        const nameA = (a.screenName || a.name || '').toLowerCase();
        const nameB = (b.screenName || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    case 'NAME_DESC':
      return sorted.sort((a, b) => {
        const nameA = (a.screenName || a.name || '').toLowerCase();
        const nameB = (b.screenName || b.name || '').toLowerCase();
        return nameB.localeCompare(nameA);
      });
    case 'PRICE_ASC':
      return sorted.sort((a, b) => {
        const priceA = Number(a.basePrice) || 0;
        const priceB = Number(b.basePrice) || 0;
        return priceA - priceB;
      });
    case 'PRICE_DESC':
      return sorted.sort((a, b) => {
        const priceA = Number(a.basePrice) || 0;
        const priceB = Number(b.basePrice) || 0;
        return priceB - priceA;
      });
    case 'NEWEST':
      return sorted.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (timeA && timeB) return timeB - timeA;
        const idA = a._id || a.id || '';
        const idB = b._id || b.id || '';
        return idB.localeCompare(idA);
      });
    default:
      return sorted;
  }
}

